import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { BaseApiService, ApiResponse, PaginatedResponse } from './baseApi';

export interface Process {
  id: number;
  processName: string;
  description: string;
  isActive: boolean;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  workAreaId: number;
}

export interface ProcessResponse extends PaginatedResponse<Process> {}

export interface ProcessCreateRequest {
  processName: string;
  description: string;
  workAreaId: number;
  isActive: boolean;
  createdBy: number;
  updatedBy: number;
}

export interface ProcessUpdateRequest extends ProcessCreateRequest {
  id: number;
}

class ProcessesApiService extends BaseApiService {
  async getProcesses(): Promise<Process[]> {
    try {
      const response = await this.makeRequest<ApiResponse<Process[]>>(
        API_ENDPOINTS.PROCESSES.LIST,
        { method: HTTP_METHODS.GET }
      );
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch processes');
    } catch (error) {
      // console.error('Error fetching processes:', error);
      toast.error('Failed to fetch processes');
      throw error;
    }
  }

  async getProcessById(id: string): Promise<Process> {
    try {
      const response = await this.makeRequest<ApiResponse<Process>>(
        API_ENDPOINTS.PROCESSES.DETAIL(id),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch process');
      }
    } catch (error) {
      // console.error('Error fetching process:', error);
      toast.error('Failed to fetch process details');
      throw error;
    }
  }

  async createProcess(processData: ProcessCreateRequest): Promise<Process> {
    try {
      const response = await this.makeRequest<ApiResponse<Process>>(
        API_ENDPOINTS.PROCESSES.CREATE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(processData)
        }
      );
      
      if (response.success) {
        toast.success('Process created successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create process');
      }
    } catch (error) {
      // console.error('Error creating process:', error);
      toast.error('Failed to create process');
      throw error;
    }
  }

  async updateProcess(id: string, processData: ProcessUpdateRequest): Promise<Process> {
    try {
      const response = await this.makeRequest<ApiResponse<Process>>(
        API_ENDPOINTS.PROCESSES.UPDATE(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(processData)
        }
      );
      
      if (response.success) {
        toast.success('Process updated successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update process');
      }
    } catch (error) {
      // console.error('Error updating process:', error);
      toast.error('Failed to update process');
      throw error;
    }
  }

  async deleteProcess(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.PROCESSES.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );
      
      if (response.success) {
        toast.success('Process deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete process');
      }
    } catch (error) {
      // console.error('Error deleting process:', error);
      toast.error('Failed to delete process');
      throw error;
    }
  }

  async searchProcesses(searchTerm: string): Promise<Process[]> {
    try {
      const response = await this.makeRequest<ApiResponse<Process[]>>(
        `${API_ENDPOINTS.PROCESSES.SEARCH}?q=${encodeURIComponent(searchTerm)}`,
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to search processes');
      }
    } catch (error) {
      // console.error('Error searching processes:', error);
      toast.error('Failed to search processes');
      throw error;
    }
  }
}

export const processesApiService = new ProcessesApiService();
